<template>
  <v-dialog v-model="dialog" fullscreen transition="dialog-bottom-transition">
    <template v-slot:activator="{ on }">
      <v-btn text v-on="on">
        <v-icon color="secondary">mdi-dots-horizontal</v-icon>
      </v-btn>
    </template>

    <v-toolbar dark color="primary" class="mx-auto">
      <v-btn icon dark @click="dialog = false">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        class="mr-5"
        color="white"
        outlined
        dark
        v-on:click="generateJpeg(patient_data.PATIENT_ID)"
      >
        <v-icon>mdi-content-save</v-icon>
        <span class="pl-3">Export</span>
      </v-btn>
    </v-toolbar>

    <v-card tile class="text-center" v-if="!displayData">
      <v-card-title class="justify-center pt-16">
        <v-icon size="96">mdi-alert-octagon-outline</v-icon>
      </v-card-title>
      <v-card-text class="text-h6">This patient has no data.</v-card-text>
      <v-card-text class="text-subtitle-2">Please select a different patient.</v-card-text>
    </v-card>
    <v-card tile v-if="displayData" :id="patient_data.PATIENT_ID">
      <v-skeleton-loader :loading="loading" height="81" width="500" type="list-item-two-line">
        <v-card-title>
          <h1 class="display-2">
            {{ patient_data.NAME }}
            <v-tooltip right>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  v-if="insurance === 'Humana' || insurance === 'Careplus'"
                  class="py-3 pl-5 float-right"
                  :color="getPatientStatus()"
                  >mdi-circle</v-icon
                >
              </template>
              <span>{{ getPatientStatusTooltip() }}</span>
            </v-tooltip>
          </h1>
        </v-card-title>
      </v-skeleton-loader>

      <v-tabs v-model="tab" background-color="transparent" grow>
        <v-tab v-for="item in items" :key="item">{{ item }}</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item v-for="item in items" :key="item">
          <template v-if="item == 'Details' && patient_data.length != 0">
            <v-row class="pa-6 pb-0">
              <v-col cols="12" sm="12" md="6" lg="3">
                <v-card elevation="2" class="stats-card">
                  <v-card-title> Patient Info </v-card-title>
                  <v-col cols="12" class="pl-0 pt-0">
                    <v-list dense class="pt-0">
                      <v-list-item>
                        <v-list-item-content>Patient ID</v-list-item-content>
                        <span>
                          {{ patient_data.PATIENT_ID }}
                        </span>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-content>Sex</v-list-item-content>
                        <span>
                          {{ patient_data.SEX }}
                        </span>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-content>DOB</v-list-item-content>
                        <span>
                          {{ patient_data.BIRTH_DATE }}
                        </span>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-content>Phone</v-list-item-content>
                        <span>
                          {{ patient_data.PHONE }}
                        </span>
                      </v-list-item>
                    </v-list>
                  </v-col>
                  <v-card-text class="pt-0 pb-0 body-1">{{ patient_data.ADDRESS }}</v-card-text>
                  <v-card-text class="pt-0 pb-0 body-1"
                    >{{ patient_data.CITY }}, {{ patient_data.STATE }}</v-card-text
                  >
                </v-card>
              </v-col>

              <v-col cols="12" sm="12" md="6" lg="3">
                <v-card elevation="2" class="stats-card">
                  <v-card-title> Membership Info </v-card-title>
                  <v-card-text class="pb-0 condensed body-1"
                    >Provider: {{ patient_data.PCP_NAME }}</v-card-text
                  >
                  <v-card-text class="pb-0 condensed body-1"
                    >Provider Location:
                    {{
                      patient_data.PCP_LOCATION_CODE ? patient_data.PCP_LOCATION_CODE : 'N/A'
                    }}</v-card-text
                  >
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content>Effective Date</v-list-item-content>
                      <v-chip>
                        {{ patient_data.ELIGIBLE_MONTH }}
                      </v-chip>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>Term Date</v-list-item-content>
                      <v-chip>
                        {{ patient_data.DISENROLLMENT_DATE }}
                      </v-chip>
                    </v-list-item>
                  </v-list>
                  <v-col cols="12" class="pl-0 pt-0"> </v-col>
                </v-card>
              </v-col>

              <v-col cols="12" sm="12" md="6" lg="3">
                <v-card elevation="2" class="stats-card">
                  <v-card-title> Finance </v-card-title>
                  <v-list dense class="pt-0">
                    <v-list-item>
                      <v-list-item-content>Risk Score C</v-list-item-content>
                      <v-chip>
                        {{ patient_data.RISKSCORE_C ? patient_data.RISKSCORE_C : 'N/A' }}
                      </v-chip>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>Risk Score D</v-list-item-content>
                      <v-chip>
                        {{ patient_data.RISKSCORE_D ? patient_data.RISKSCORE_D : 'N/A' }}
                      </v-chip>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>Total Expenses</v-list-item-content>
                      <v-chip>
                        ${{
                          patient_data.TOTAL_EXPENSES
                            ? formatCurrency(patient_data.TOTAL_EXPENSES)
                            : 'N/A'
                        }}
                      </v-chip>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>Total Revenue</v-list-item-content>
                      <v-chip>
                        ${{
                          patient_data.TOTAL_REVENUE
                            ? formatCurrency(patient_data.TOTAL_REVENUE)
                            : 'N/A'
                        }}
                      </v-chip>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>MLR</v-list-item-content>
                      <v-chip dark :color="getMLRColor(patient_data.MLR)">
                        {{ patient_data.MLR }}%
                      </v-chip>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-col>

              <v-col cols="12" sm="12" md="6" lg="3">
                <v-card elevation="2" class="stats-card">
                  <v-card-title> Utilization </v-card-title>
                  <v-skeleton-loader
                    :loading="loadingUtilization"
                    max-width="275"
                    type="list-item, list-item, list-item, list-item"
                  >
                    <v-list dense class="pt-0">
                      <v-list-item>
                        <v-list-item-content>ER Visits</v-list-item-content>
                        <v-chip>
                          {{ patient_utilization.er_visits }}
                        </v-chip>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-content>Admissions</v-list-item-content>
                        <v-chip>
                          {{ patient_utilization.admissions }}
                        </v-chip>
                      </v-list-item>
                      <!-- <v-list-item>
                        <v-list-item-content>PCP Claims</v-list-item-content>
                        <v-chip> {{ patient_utilization.pcp_claims }} </v-chip>
                      </v-list-item> -->
                      <v-list-item>
                        <v-list-item-content>Specialist Claims</v-list-item-content>
                        <v-chip>
                          {{ patient_utilization.specialists }}
                        </v-chip>
                      </v-list-item>
                    </v-list>
                  </v-skeleton-loader>
                </v-card>
              </v-col>
            </v-row>
            <v-row class="pa-6 pt-0">
              <v-col cols="12">
                <PatientConditions
                  :patient="patient_id ? patient_id : null"
                  :insurance="insurance ? insurance : null"
                />
              </v-col>

              <v-col cols="12">
                <HCC :patient="patient_id" :insurance="insurance" />
              </v-col>

              <v-col cols="12">
                <institutional :patient="patient_id" :insurance="insurance" />
              </v-col>

              <v-col cols="12">
                <professional :patient="patient_id" :insurance="insurance" />
              </v-col>

              <v-col cols="12">
                <Rx :patient="patient_id" :insurance="insurance" />
              </v-col>

              <v-col cols="12">
                <laboratory :patient="patient_id" :insurance="insurance" />
              </v-col>
            </v-row>
          </template>

          <v-row v-if="item == 'Institutional'" class="pa-6">
            <v-col cols="12">
              <institutional :patient="patient_id" :insurance="insurance" />
            </v-col>
          </v-row>

          <v-row v-if="item == 'Professional'" class="pa-6">
            <v-col cols="12">
              <professional :patient="patient_id" :insurance="insurance" />
            </v-col>
          </v-row>

          <v-row v-if="item == 'Rx'" class="pa-6">
            <v-col cols="12">
              <rx :patient="patient_id" :insurance="insurance" />
            </v-col>
          </v-row>

          <v-row v-if="item == 'Laboratory'" class="pa-6">
            <v-col cols="12">
              <laboratory :patient="patient_id" :insurance="insurance" />
            </v-col>
          </v-row>

          <v-row v-if="item == 'Open Conditions'" class="pa-6">
            <v-col cols="12">
              <OpenConditions :patient="patient_id" :insurance="insurance" />
            </v-col>
          </v-row>

          <v-row v-if="item == 'Quality'" class="pa-6">
            <v-col cols="12">
              <QualityGaps :patient="patient_id" :insurance="insurance" />
            </v-col>
          </v-row>

          <v-row v-if="item === 'Tasks'">
            <patient-tasks :enrolleeID="patient_id" :key="patientTasksKey" />
          </v-row>

          <v-row v-if="item == 'History'" class="pa-6">
            <v-col cols="12">
              <History :patient="patient_id" :insurance="insurance" />
            </v-col>
          </v-row>

          <!-- <v-row v-if="item == 'Notifications'" class="pa-6">
            <v-col cols="12">
              <Notifications
                :startDate="notificationsStartDate"
                :endDate="notificationsEndDate"
                :insurance="insurance"
                :patient="insurance === 'Careplus' ? patient_mbi : patient_id"
              />
            </v-col>
          </v-row> -->
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import * as htmlToImage from 'html-to-image';
import PatientConditions from './patientinfo/PatientConditions.vue';
import Institutional from './patientinfo/Institutional.vue';
import Professional from './patientinfo/Professional.vue';
import Rx from './patientinfo/Rx.vue';
import Laboratory from './patientinfo/Laboratory.vue';
import HCC from './patientinfo/HCC.vue';
import OpenConditions from './patientinfo/OpenConditions.vue';
import QualityGaps from './patientinfo/QualityGaps.vue';
import History from './patientinfo/History.vue';
// import Notifications from './notificationsComponents/Notifications.vue';
import PatientTasks from './enrollment-components/PatientTasks.vue';

export default {
  name: 'PatientModal',
  props: ['patient', 'insurance', 'getID'],
  data() {
    return {
      dialog: false,
      patientTasksKey: 0,
      loading: false,
      loadingUtilization: false,
      tab: null,
      patient_id: null,
      patient_data: [],
      patient_utilization: [],
      patient_mbi: null,
      items: [
        'Details',
        'Institutional',
        'Professional',
        'Rx',
        'Laboratory',
        'Open Conditions',
        'Quality',
        'Tasks',
        'History',
        // 'Notifications',
      ],
      notificationsStartDate: new Date(new Date().getFullYear(), new Date().getMonth() - 3)
        .toISOString()
        .substr(0, 10),
      notificationsEndDate: new Date().toISOString().substr(0, 10),
      displayData: true,
      link: '',
      // chronicConditions: [
      //   { name: "Asthma", icon: "Asthma.svg" },
      //   { name: "Diabetes", icon: "DiabetesColor.svg" },
      //   { name: "CHF", icon: "CHFColor.svg" },
      //   { name: "Arthtitis", icon: "Arthritis.svg" },
      //   { name: "CVD", icon: "CVDColor.svg" },
      //   { name: "Hypertension", icon: "HypertensionColor.svg" },
      //   { name: "COPD", icon: "COPD.svg" }
      // ],
      // diagConditions: [
      //   { name: "Infectious", icon: "InfectiousDiseaseColor.svg" },
      //   { name: "Circulatory", icon: "Cirulatory.svg" },
      //   { name: "Nervous", icon: "NervousSystemColor.svg" },
      //   { name: "Abnormal", icon: "Abnormal.svg" },
      //   { name: "Neoplasms", icon: "NeoplasmsColor.svg" },
      //   { name: "Respiratory", icon: "RespiratoryColor.svg" },
      //   { name: "Musculoskeletal", icon: "MusculoskeletalColor.svg" },
      //   { name: "Injury", icon: "Injury.svg" },
      //   { name: "Endocrine", icon: "EndocrineColor.svg" },
      //   { name: "Digestive", icon: "DigestiveColor.svg" },
      //   { name: "Skin", icon: "SkinColor.svg" },
      //   { name: "Blood organs", icon: "BloodColor.svg" },
      //   { name: "Urinary", icon: "UrinaryColor.svg" },
      //   { name: "Mental", icon: "MentalIllnessColor.svg" },
      //   { name: "Pregnancy", icon: "PerinatalColor.svg" },
      //   { name: "Congenital", icon: "Congenital.svg" },
      //   { name: "Eye/Ear", icon: "EndocrineColor.svg" }
      // ],
    };
  },
  components: {
    PatientConditions,
    Institutional,
    Professional,
    Rx,
    Laboratory,
    HCC,
    OpenConditions,
    PatientTasks,
    QualityGaps,
    History,
    // Notifications,
  },
  methods: {
    generateJpeg(id) {
      htmlToImage.toJpeg(document.getElementById(id), { quality: 0.95 }).then((dataUrl) => {
        this.link = document.createElement('a');
        this.link.download = 'Patient_Snapshot' + '.jpeg';
        this.link.href = dataUrl;
        this.link.click();
      });
    },
    async getPatient() {
      this.loading = true;
      this.loadingUtilization = true;

      // if patient_id is passed to modal
      this.patient_data = [];
      this.patient_mbi = null;
      const url = `api/patient/modal/insurance=${this.insurance}/${this.patient_id}`;
      axios.get(url).then((response) => {
        if (response.data[0]) {
          const [patientData] = response.data;
          this.patient_data = patientData;
          this.displayData = true;
        } else {
          this.displayData = false;
        }
        this.loading = false;
      });

      axios.get(`api/patient/utilization/${this.insurance}/${this.patient_id}`).then((response) => {
        this.patient_utilization = response.data;
        this.loadingUtilization = false;
      });
    },
    formatCurrency(c) {
      if (c !== undefined) {
        return parseFloat(c)
          .toFixed(2)
          .replace(/\d(?=(\d{3})+\.)/g, '$&,');
      }
      return c;
    },
    getPatientStatus() {
      return 'error';
    },
    getPatientStatusTooltip() {
      return 'Termed';
    },
    getMLRColor(MLR) {
      if (MLR > 1) return 'red lighten-1';
      return 'green lighten-1';
    },
  },
  watch: {
    async dialog() {
      // make sure dialog is open before grabbing patient_id
      if (this.dialog === true) {
        if (this.patient.ID != null) {
          this.patient_id = this.patient.ID;
        } else if (this.patient != null) {
          this.patient_id = this.patient;
        }
      }
    },
    async patient_id() {
      if (this.patient_id != null) {
        this.getPatient();
      }
    },
    tab() {
      if (this.tab === 4) {
        this.patientTasksKey += 1;
      }
    },
  },
};
</script>

<style>
.v-dialog--fullscreen {
  overflow-x: hidden !important;
}

.stats-card {
  min-height: 285px;
}
</style>
